import {
  QuestionareCsvRequest,
  SmartAnalyzeService,
} from '@/api/services/SmartAnalyzeService';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import Markdown from 'markdown-to-jsx';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const FormSchema = z.object({
  question: z.string().min(2, {
    message: 'Your question is required!',
  }),
});

export const SmartAnalyzePage = () => {
  const org = useStore($org);
  const queryParams = new URLSearchParams(window.location.search); // To get a specific query parameterconst
  const paramValue = queryParams.get('paramName');
  const paramsObject = Object.fromEntries(queryParams.entries());

  const analyzeCsvRef = useRef<HTMLDivElement | null>(null);
  const analyzeCsvQuestionareRef = useRef<HTMLDivElement | null>(null);

  const [analyzingCsvStep1, setAnalyzingCsvStep1] = useState(false);
  const [smartAnalyzeStep1Result, setSmartAnalyzeStep1Result] = useState<
    string | null | undefined
  >('');
  const [categoricalSummary, setCategoricalSummary] = useState<any>({});

  const [processingQuestionare, setProcessingQuestionare] = useState(false);

  const [smartAnalyzeQuestionareResult, setSmartAnalyzeQuestionareResult] =
    useState<string | null>('');

  const [smartAnalyzeFileToUpload, setSmartAnalyzeFileToUpload] = useState<
    File | undefined
  >();
  const [chatHistory, setChatHistory] = useState<any>();

  const [csvRowCount, setCsvRowCount] = useState<number>(0);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      question: '',
    },
  });

  function smartAnalyzeCsvWithFile() {
    if (!smartAnalyzeFileToUpload) return;
    setAnalyzingCsvStep1(true);
    if (org?.id) {
      smartAnalyzeCsv(org?.id, undefined, smartAnalyzeFileToUpload)
        .then(res => {
          if (res?.succeeded) {
            setSmartAnalyzeStep1Result(res?.analyzation);
            setCategoricalSummary(res?.categoricalSummary);
            setChatHistory(res?.chatHistory);
            analyzeCsvRef?.current?.scrollIntoView({ behavior: 'smooth' });
            toast('Data analyzed successfully!', {
              duration: 5000,
            });
          } else {
            toast.error(`Failed to analyze data :: ${res?.message}`, {
              duration: 5000,
            });
          }
        })
        .catch(() => {
          toast.error(`Failed to analyze data.`, {
            duration: 5000,
          });
        })
        .finally(() => {
          setAnalyzingCsvStep1(false);
        });
    }
  }

  function smartAnalyzeCsvQuestionareAskQuestion(
    data: z.infer<typeof FormSchema>,
  ) {
    if (smartAnalyzeStep1Result) {
      setProcessingQuestionare(true);
      const request: QuestionareCsvRequest = {
        question: data.question,
        categoricalSummary: categoricalSummary,
        chatHistory: chatHistory,
      };
      SmartAnalyzeService.questionareCsv(request)
        .then(res => {
          setSmartAnalyzeQuestionareResult(res?.output!);
          setChatHistory(res?.chatHistory);
          analyzeCsvQuestionareRef?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });
          toast('Data analyzed successfully!', {
            duration: 5000,
          });
        })
        .catch(() => {
          toast.error('Failed to analyze data!', {
            duration: 5000,
          });
        })
        .finally(() => {
          setProcessingQuestionare(false);
        });
    }
  }

  useEffect(() => {
    if (paramsObject?.projectId) {
      // execute the run function
      setAnalyzingCsvStep1(true);
      if (org?.id) {
        smartAnalyzeCsv(org?.id, paramsObject?.projectId)
          .then(res => {
            if (res?.succeeded) {
              setSmartAnalyzeStep1Result(res?.analyzation);
              setCategoricalSummary(res?.categoricalSummary);
              setChatHistory(res?.chatHistory);
              analyzeCsvRef?.current?.scrollIntoView({ behavior: 'smooth' });
              toast('Data analyzed successfully!', {
                duration: 5000,
              });
            } else {
              toast.error(`Failed to analyze data :: ${res?.message}`, {
                duration: 5000,
              });
            }
          })
          .catch(() => {
            toast.error('Failed to analyze data!', {
              duration: 5000,
            });
          })
          .finally(() => {
            setAnalyzingCsvStep1(false);
          });
      }
    }
  }, []);

  useEffect(() => {
    if (analyzeCsvRef.current) {
      analyzeCsvRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [smartAnalyzeStep1Result]);

  useEffect(() => {
    if (analyzeCsvQuestionareRef.current) {
      analyzeCsvQuestionareRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [smartAnalyzeQuestionareResult]);

  return (
    <div>
      <div className=" mt-2 flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Smart Analyze</h2>
        </div>
      </div>

      <div className="mt-12 flex h-auto w-full flex-col items-center">
        {/* <iframe
          src={`https://tennis-rally-streamlit-csv-xyvz4l5nva-uk.a.run.app?embed=true&embed_options=show_toolbar&embed_options=show_footer${paramsObject?.csv ? `&csv=${paramsObject?.csv}` : ''}`}
          //height="650"
          className="h-[80vh] w-full border-0"
          // style={{ width: '100%', border: 'none' }}
        ></iframe> */}

        <div className="grid h-[80px] w-8/12 items-center gap-2">
          <Label htmlFor="csvfile">Upload a CSV file for analysis</Label>
          <div className="flex h-full items-center justify-center gap-2">
            <Input
              id="csvfile"
              type="file"
              className="h-full w-full items-center justify-center rounded-lg bg-slate-100 p-4 text-center text-foreground dark:text-primary-foreground"
              accept=".csv"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  // Read the file to count rows
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    const text = event.target?.result as string;
                    const rowCount = text.split('\n').length - 1; // -1 for header row
                    setCsvRowCount(rowCount);
                    
                    // if (rowCount > 1000) {
                    //   toast.error('File exceeds 1000 rows limit. Please filter your data and try again.', {
                    //     duration: 5000,
                    //   });
                    // } 
                    // else {
                      setSmartAnalyzeFileToUpload(file);
                    // }
                  };
                  reader.readAsText(file);
                }
              }}
            />

            <Button
              className="h-[80%] w-[20%] rounded-lg"
              onClick={() => {
                smartAnalyzeCsvWithFile();
              }}
              disabled={analyzingCsvStep1 
                // || csvRowCount > 1000
              }
            >
              {analyzingCsvStep1 ? 'Analyzing...' : 'Analyze'}
            </Button>
          </div>
        </div>

        {analyzingCsvStep1 && (
          <div
            className="mt-6 flex w-8/12 flex-col space-y-3"
            ref={analyzeCsvRef}
          >
            <Skeleton className="h-[125px] w-full rounded-xl" />
            <div className="w-full space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-[125px] w-full rounded-xl" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        )}

        {smartAnalyzeStep1Result && !analyzingCsvStep1 && (
          <div className="mt-6 w-8/12 rounded-md">
            <Markdown className="prose w-full">
              {smartAnalyzeStep1Result}
            </Markdown>
          </div>
        )}

        {smartAnalyzeStep1Result && !analyzingCsvStep1 && (
          <div className="mt-6 w-8/12 rounded-md">
            <h2 className="scroll-m-20 pb-2 text-3xl font-semibold tracking-tight first:mt-0">
              Ask anything about the CSV
            </h2>
            <p className="text-sm leading-6 text-muted-foreground [&:not(:first-child)]:mt-2">
              Example: You can ask it to summarize the results in a specific
              format or ask how many instances of udaap issues you found.
            </p>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(
                  smartAnalyzeCsvQuestionareAskQuestion,
                )}
                className="mt-4 w-full space-y-6"
              >
                <FormField
                  control={form.control}
                  name="question"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Question"
                          {...field}
                          disabled={processingQuestionare}
                        />
                      </FormControl>
                      <FormDescription>
                        {processingQuestionare
                          ? 'Analyzing...'
                          : 'Press enter to ask the question'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        )}

        {processingQuestionare && (
          <div
            className="mt-6 flex w-8/12 flex-col space-y-3"
            ref={analyzeCsvQuestionareRef}
          >
            <Skeleton className="h-[80px] w-full rounded-xl" />
            <div className="w-full space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-[125px] w-full rounded-xl" />
            </div>
          </div>
        )}

        {smartAnalyzeQuestionareResult &&
          !processingQuestionare &&
          !analyzingCsvStep1 && (
            <div className="mt-4 w-8/12 rounded-md">
              <Markdown className="prose w-full">
                {smartAnalyzeQuestionareResult}
              </Markdown>
            </div>
          )}
      </div>
    </div>
  );
};
